import {
  DialogFragment,
  PageFragment,
  PageFragmentProps,
  PageFragmentState,
  PageFragmentStyleFlags
} from "@/shared/PageFragment";
import React, {createRef} from "react";
import {LabelerTask, LabelerTaskCase, LabelerTaskCaseStatus} from "@/fyneai-shared/types";
import {IconButton, Tooltip, Typography} from "@mui/material";
import {StyledBoxRow, StyledLoading} from "@/shared/StyledComponents";
import {Action, ACTION_SPACE, ActionBase} from "@/shared/types";
import {WorkbenchCaseFragment} from "./WorkbenchCaseFragment";
import {SZ_XXLG} from "@/shared/dimens";
import {ArrowBackIosOutlined, ArrowForwardIosOutlined} from "@mui/icons-material";
import {BaseApp} from "../../../shared/BaseApp";
import {sleep} from "@/shared/sleep";

export type WorkbenchFragmentProps = PageFragmentProps & {
  task: LabelerTask,
  initialSelection?: LabelerTaskCase,
  onFinish: () => void,
  showNavigation?: boolean,
}

type WorkbenchFragmentState = PageFragmentState & {
  updating?: boolean,
  caseIndex: number,
}

export class WorkbenchFragment extends DialogFragment<WorkbenchFragmentProps, WorkbenchFragmentState> {

  static initialCaseIndex(task: LabelerTask, initialSelection?: LabelerTaskCase): number {
    let index: number;
    if (initialSelection) {
      index = task.cases.findIndex(case_ => case_.id === initialSelection.id);
      if (index >= 0) {
        return index;
      }
    }
    return (initialSelection && (task.cases.findIndex(case_ => case_.id === initialSelection.id) || 0))
      || (task.cases.findIndex(case_ => case_.status === LabelerTaskCaseStatus.UNLABALED || case_.status === LabelerTaskCaseStatus.RETURNED) || 0);
  }

  private readonly caseFragmentRef = createRef<WorkbenchCaseFragment>();

  protected onCreateState(props?: WorkbenchFragmentProps): WorkbenchFragmentState {
    return {
      ...super.onCreateState(props),
      title: `${props.task?.name || "Task"}`,
      caseIndex: WorkbenchFragment.initialCaseIndex(props.task, props.initialSelection),
    };
  }

  componentDidMount() {
    super.componentDidMount();
    if (!(this.state.caseIndex >= 0)) {
      this.props.onFinish();
      return;
    }
    this.loadCurrentCase();
  }

  componentDidUpdate(prevProps: Readonly<WorkbenchFragmentProps>, prevState: Readonly<WorkbenchFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.caseIndex !== this.state.caseIndex) {
      this.loadCurrentCase();
    }
  }

  private async loadCurrentCase(): Promise<void> {
    if (!(this.state.caseIndex >= 0)) {
      return;
    }
    this.setState({
      updating: true,
    });
    await this.props.task.cases[this.state.caseIndex].load();
    this.setState({
      updating: false,
    });
  }

  protected styleFlags(): number | PageFragmentStyleFlags {
    return PageFragment.STYLE_ACTION_PANEL_TYPE_BUTTONSTRIP_FLAG | PageFragment.STYLE_TOOLBAR_TYPE_FLAG;
  }

  protected getToolbar(toolbarModeId: string | null): React.ReactElement {
    return <>
      <Typography style={{minWidth: SZ_XXLG}}>
        {`Case ${this.state.caseIndex + 1} of ${this.props.task.cases.length}`}
      </Typography>
      {this.props.showNavigation
        ? <StyledBoxRow>
          <Tooltip title={"Previous Case"}>
            <IconButton
              onClick={() => this.setState({caseIndex: this.state.caseIndex - 1})}
              disabled={this.state.caseIndex <= 0}
            >
              <ArrowBackIosOutlined/>
            </IconButton>
          </Tooltip>
          <Tooltip title={"Next Case"}>
            <IconButton
              onClick={() => this.setState({caseIndex: this.state.caseIndex + 1})}
              disabled={this.state.caseIndex >= this.props.task.cases.length - 1}
            >
              <ArrowForwardIosOutlined/>
            </IconButton>
          </Tooltip>
        </StyledBoxRow>
        : null}
    </>;
  }

  private gotoNext(): void {
    if (this.state.caseIndex < this.props.task.cases.length - 1) {
      this.setState({caseIndex: this.state.caseIndex + 1})
    } else {
      this.props.onFinish();
    }
  }

  protected getActionButtonstrip(): ActionBase[] {
    return [
      ACTION_SPACE,
      new Action("Skip Case", () => {
        this.gotoNext();
      }).setVariant("contained"),
      new Action("Submit Case", () => {
        const gotoNext = this.gotoNext.bind(this);
        const case_ = this.props.task.cases[this.state.caseIndex];
        const labels = this.caseFragmentRef.current.getTaskCaseLabels();
        BaseApp.CONTEXT.showProgressDialog("Submitting...", null, async function () {
          await sleep(1000);
          case_.labels.splice(0, case_.labels.length);
          case_.labels.push(...labels);
          case_.status = case_.getTask().requireReview ? LabelerTaskCaseStatus.SUBMITTED : LabelerTaskCaseStatus.ACCEPTED;
          await case_.save();
          gotoNext();
        });
      }).setVariant("contained").makeSecondary(),
    ]
  }

  renderContent(): React.ReactElement {
    if (!(this.state.caseIndex >= 0)) {
      return null;
    }
    if (this.state.updating) {
      return <StyledLoading/>;
    }
    return <WorkbenchCaseFragment
      ref={this.caseFragmentRef}
      key={`case_${this.state.caseIndex}`}
      case={this.props.task.cases[this.state.caseIndex]}/>
  }
}
